import { Fragment, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { Menu, Transition } from "@headlessui/react";
import {
  Bars3BottomLeftIcon,
  LifebuoyIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import Avatar from "../../user/Avatar";
import classNames from "../../../utils/classNames";
import { useAuthApi } from "../../../hooks/useAuthApi";
import CurrentBasket from "../../basket/CurrentBasket";
import { UsageTopup } from "../../usage/UsageTopup";
import useCustomerDetails from "../../../data/useCustomerDetails";
import useTickets from "../../../data/useTickets";
import { TicketStatus } from "../../../models/Ticket";
import { useAuth } from "../../../contexts/AuthContext";
import { allVisibleTopNav } from "../../../auth/topNav";
import AppNotifications from "../../notifications/AppNotifications";
import OmniSearch from "./OmniSearch";
import LanguageSelector from "./LanguageSelector";
import ContextualHelpButton from "./ContextualHelpButton";

export interface TopbarProps {
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function Topbar({ collapsed, onToggleCollapsed, setSidebarOpen }: TopbarProps) {
  const { t } = useTranslation("common");
  const { logout } = useAuthApi();
  const { brand } = useCustomerDetails();
  const brandLogo = brand.data?.data.logo;
  const brandCroppedLogo = brand.data?.data.croppedLogo;
  const navigate = useNavigate();
  const { userId, topNav } = useAuth();
  const show = topNav ?? allVisibleTopNav();
  const { tickets } = useTickets();

  const hasNewSupportReply = useMemo(() => {
    const rows = tickets.data?.data ?? [];

    return rows.some((ticket) => {
      if (
        !userId ||
        ticket.createdByUserId.toLowerCase() !== userId.toLowerCase() ||
        ticket.status !== TicketStatus.Open ||
        !ticket.notes?.length
      ) {
        return false;
      }

      const latestNote = [...ticket.notes].sort(
        (a, b) => +new Date(b.created) - +new Date(a.created)
      )[0];

      return latestNote?.isFromStaff === true;
    });
  }, [tickets.data?.data, userId]);

  const handleLogout = async () => {
    // TODO: impl logout with sso
    // For now manual cookie deletion
    // // Delete the cookie by setting an expiration date in the past
    // document.cookie = "WilmaAuthToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

    // // Optionally redirect to a login page or home page after logout
    // window.location.href = "/"; // Update with the correct path for your app
    logout().then(() => {
      window.location.href = "/";
    });
  };

  const passwordReset = () => {
    //TODO: impl password reset
  };

  const userNavigation = [
    { name: t("Your Profile"), onClick: () => navigate("/profile") },
    { name: t("Reset password"), onClick: passwordReset },
    { name: t("Sign out"), onClick: handleLogout },
  ];

  return (
    <>
      <div className="sticky top-0 z-30 flex h-16 flex-shrink-0 bg-white shadow">
        {/* For mobile sidebar */}
        <button
          type="button"
          className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 md:hidden"
          onClick={() => setSidebarOpen(true)}
        >
          <span className="sr-only">{t("Open sidebar")}</span>
          <Bars3BottomLeftIcon className="h-6 w-6" aria-hidden="true" />
        </button>


        <div className="flex flex-1 justify-between">

          <div className="flex flex-shrink-0 items-center justify-center">
            {/* For desktop collapsing */}
            <button
              type="button"
              className="border-r border-gray-200 hover:bg-gray-100 px-4 h-full w-16 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 md:block hidden"
              onClick={onToggleCollapsed}
            >
              <span className="sr-only">{t("Collapse Sidebar")}</span>
              <Bars3BottomLeftIcon className="h-6 w-6" aria-hidden="true" />
            </button>
            <div className={`px-4`}>
              <img className="h-10 hidden md:block w-auto object-contain" src={brandLogo} alt="Company Logo" />
              <img className="h-8 md:hidden w-auto object-contain" src={brandCroppedLogo} alt="Company Logo" />
            </div>
          </div>

          {show.search ? <OmniSearch /> : <div className="flex-1" />}

          <div className="mx-4 flex items-center gap-x-4 md:ml-6">
            {show.help ? <ContextualHelpButton /> : null}

            <button
              type="button"
              onClick={() => navigate("/tickets")}
              className="relative inline-flex items-center gap-x-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              <LifebuoyIcon className="h-5 w-5" aria-hidden="true" />
              <span className="hidden md:inline">{t("Support")}</span>
              {hasNewSupportReply ? (
                <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white" />
              ) : null}
            </button>

            {/* <button
              type="button"
              className="rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <span className="sr-only">View notifications</span>
              <BellIcon className="h-6 w-6" aria-hidden="true" />
            </button> */}
            {show.notifications ? <AppNotifications /> : null}

            {show.usage ? <UsageTopup /> : null}

            {show.basket ? <CurrentBasket /> : null}

            {/* Hidden until the Polish translations are ready to go live */}
            <LanguageSelector />

            {/* Profile dropdown */}
            <Menu as="div" className="relative">
              <div>
                <Menu.Button className="flex max-w-xs items-center rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-transform hover:scale-110">
                  <span className="sr-only">{t("Open user menu")}</span>
                  <Avatar classes="h-9 w-9" />
                </Menu.Button>
              </div>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {userNavigation.map((item) => (
                    <Menu.Item key={item.name}>
                      {({ active }) => (
                        <a
                          // href={item.href}
                          onClick={item.onClick}
                          className={classNames(
                            active ? "bg-gray-100" : "",
                            "block px-4 py-2 text-sm text-gray-700 cursor-pointer"
                          )}
                        >
                          {item.name}
                        </a>
                      )}
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>
    </>
  );
}
